// Периоды отчётов отдела продаж — по московскому календарю, как считает сервер.

const MSK_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Moscow',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function moscowToday(): string {
  return MSK_DAY.format(new Date());
}

function shiftDay(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
}

function monthStart(date: string, deltaMonths = 0): string {
  const [y, m] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1 + deltaMonths, 1)).toISOString().slice(0, 10);
}

export type PeriodPreset = 'month' | 'prev' | 'quarter' | 'year' | 'custom';

export interface PeriodValue {
  preset: PeriodPreset;
  from: string;
  to: string;
}

export const PRESET_LABELS: Record<PeriodPreset, string> = {
  month: 'Этот месяц',
  prev: 'Прошлый',
  quarter: '3 месяца',
  year: 'С начала года',
  custom: 'Даты',
};

export function presetPeriod(preset: Exclude<PeriodPreset, 'custom'>, today = moscowToday()): PeriodValue {
  switch (preset) {
    case 'month':
      return { preset, from: monthStart(today), to: today };
    case 'prev':
      return { preset, from: monthStart(today, -1), to: shiftDay(monthStart(today), -1) };
    case 'quarter':
      return { preset, from: monthStart(today, -2), to: today };
    case 'year':
      return { preset, from: `${today.slice(0, 4)}-01-01`, to: today };
  }
}

// Один период на сводку и расходы: переход между разделами его не сбрасывает.
let remembered: PeriodValue | null = null;

export function recallPeriod(): PeriodValue {
  if (!remembered) return presetPeriod('month');
  return remembered.preset === 'custom' ? remembered : presetPeriod(remembered.preset);
}

export function rememberPeriod(v: PeriodValue): void {
  remembered = v;
}

const utc = (d: string) => new Date(`${d}T00:00:00Z`);
const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' });
const YEAR = new Intl.DateTimeFormat('ru-RU', { year: 'numeric', timeZone: 'UTC' });

/** «1–3 октября 2026», «28 сентября – 3 октября 2026», «12 декабря 2025 – 3 января 2026». */
export function periodLabel(from: string, to: string): string {
  const year = (d: string) => YEAR.format(utc(d)).replace(/\s*г\.?$/, '');
  if (from === to) return `${DAY_MONTH.format(utc(from))} ${year(from)}`;
  if (from.slice(0, 7) === to.slice(0, 7)) {
    return `${Number(from.slice(8))}–${DAY_MONTH.format(utc(to))} ${year(to)}`;
  }
  if (from.slice(0, 4) === to.slice(0, 4)) {
    return `${DAY_MONTH.format(utc(from))} – ${DAY_MONTH.format(utc(to))} ${year(to)}`;
  }
  return `${DAY_MONTH.format(utc(from))} ${year(from)} – ${DAY_MONTH.format(utc(to))} ${year(to)}`;
}

const MONTH_SHORT = new Intl.DateTimeFormat('ru-RU', { month: 'short', timeZone: 'UTC' });
const MONTH_LONG = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** «2026-10» → «окт». */
export function monthShort(ym: string): string {
  return MONTH_SHORT.format(utc(`${ym}-01`)).replace('.', '');
}

/** «2026-10» → «Октябрь 2026». */
export function monthLong(ym: string): string {
  const s = MONTH_LONG.format(utc(`${ym}-01`)).replace(/\s*г\.?$/, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Изменение к прошлому периоду в процентах; null — сравнивать не с чем. */
export function deltaPercent(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}
